import { useMemo, useState } from "react";
import { RefreshCw, Database, Users as UsersIcon, Download, Loader2, PlayCircle } from "lucide-react";
import { dateTime } from "@/lib/format";
import "@/components/settings/DataPipelineDashboard.css";

const TABS = ["Update History", "Live Monitor", "Error Logs", "API Usage", "System Status"];
const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString());
const pct = (done, total) => (total ? Math.min(100, (done / total) * 100) : 0);
const pctText = (v) => (v >= 100 ? "100" : v.toFixed(1));

// Supported manual tools map to REAL backend endpoints. Unsupported ones stay disabled (no fake success).
const SUPPORTED_TOOLS = [
  { id: "update", label: "Update Now", sub: "Daily fetch (scores, box)" },
  { id: "backfill", label: "Fix Missing Games", sub: "Resume/backfill box scores" },
  { id: "lineups", label: "Sync Lineups", sub: "Refresh today's starting fives" },
  { id: "starters", label: "Load Lineup History", sub: "Backfill past starters" },
];
const UNSUPPORTED_TOOLS = [
  { id: "rebuildPlayerStats", label: "Rebuild Player Stats" },
  { id: "rebuildTeamStats", label: "Rebuild Team Stats" },
  { id: "syncOdds", label: "Sync Odds" },
  { id: "clearCache", label: "Clear Cache" },
];

function DetailsModal({ job, onClose }) {
  if (!job) return null;
  return (
    <div className="nc-modal-back" onClick={onClose} data-testid="job-details-modal">
      <div className="nc-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{job.type} · {dateTime(job.run_at)}</h3>
        <p className="nc-muted">Calls {fmt(job.calls_used)} · Games {fmt(job.games_added)} · Rows {fmt(job.rows_added)}</p>
        {job.errors?.length ? job.errors.map((e, i) => <p className="nc-errl" key={i}>• {e}</p>) : <p className="nc-muted">No errors.</p>}
        <pre>{JSON.stringify(job.details || {}, null, 2)}</pre>
        <button className="primary" onClick={onClose} data-testid="job-details-close">Close</button>
      </div>
    </div>
  );
}

export function DataPipelineDashboard({ status, logs, teams, pending, onTask, onExport, children }) {
  const [tab, setTab] = useState("Update History");
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [team, setTeam] = useState("DAL");
  const [tmTeam, setTmTeam] = useState("");
  const [tmSeason, setTmSeason] = useState("2026-27");
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);

  const busy = !!pending;
  const jobs = status?.jobs || {};
  const runningJobs = Object.entries(jobs).filter(([, j]) => j?.running);

  const rows = useMemo(() => {
    let r = (logs || []).map((l, i) => ({ ...l, _i: i, _status: l.errors?.length ? "error" : "success" }));
    if (tab === "Error Logs") r = r.filter((l) => l.errors?.length);
    if (type !== "all") r = r.filter((l) => (l.type || "").toLowerCase() === type);
    if (search) { const q = search.toLowerCase(); r = r.filter((l) => `${l.type} ${l.details?.trigger || ""}`.toLowerCase().includes(q)); }
    return r;
  }, [logs, tab, type, search]);

  const exportCsv = async () => { setExporting(true); try { await onExport({ team }); } finally { setExporting(false); } };

  const regularCap = status ? status.daily_cap - status.live_reserve : 0;
  const regularLeft = status ? Math.max(0, regularCap - status.calls_today) : 0;
  const liveLeft = status ? Math.max(0, Math.min(status.live_reserve, status.daily_cap - status.calls_today)) : 0;

  return (
    <div className="nc-pipeline" data-testid="settings-page">
      <p className="nc-breadcrumb">Home<span>›</span>Settings &amp; Log<span>›</span>Data Pipeline</p>
      <div className="nc-header">
        <div>
          <p className="nc-eyebrow">Settings &amp; Log</p>
          <h1>DATA <em>PIPELINE</em></h1>
          <p className="nc-muted">Manage data fetching, processing and system status.</p>
        </div>
        <div className="nc-header-actions">
          <button className="primary" data-testid="update-now-btn" disabled={busy || jobs.daily?.running} onClick={() => onTask("update")}>
            {pending === "update" || jobs.daily?.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}{jobs.daily?.running ? "Updating…" : "Update Now"}
          </button>
          <button data-testid="backfill-btn" disabled={busy || jobs.backfill?.running} onClick={() => onTask("backfill")}>
            {pending === "backfill" || jobs.backfill?.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}{jobs.backfill?.running ? "Backfill running" : "Run / Resume Backfill"}
          </button>
          <button data-testid="starters-job-btn" disabled={busy || jobs.starters?.running} onClick={() => onTask("starters")}>
            {pending === "starters" || jobs.starters?.running ? <Loader2 className="h-4 w-4 animate-spin" /> : <UsersIcon className="h-4 w-4" />}{jobs.starters?.running ? "Lineup history running" : "Load Lineup History"}
          </button>
        </div>
      </div>

      {status && (
        <section className="nc-summary">
          <article className="nc-panel" data-testid="calls-today-card">
            <h2>API Calls Today <small style={{ color: "#7294b5", textTransform: "none" }}>(UTC {status.utc_date})</small></h2>
            <div className="nc-big"><span data-testid="calls-today-value">{fmt(status.calls_today)}</span><small> / {fmt(status.daily_cap)}</small></div>
            <div className="nc-progress"><span style={{ width: `${pct(status.calls_today, status.daily_cap)}%` }} /></div>
            <div className="nc-mini-grid">
              <div><span>Regular left</span><strong data-testid="regular-calls-left">{fmt(regularLeft)}<small> / {fmt(regularCap)}</small></strong></div>
              <div className={liveLeft < status.live_reserve ? "warn" : ""}><span>Live reserve left</span><strong data-testid="live-reserve-left">{fmt(liveLeft)}<small> / {fmt(status.live_reserve)}</small></strong></div>
            </div>
            <p className="nc-muted" style={{ marginTop: 10 }}>Resets daily at 03:00 Athens. Status: {runningJobs.length ? "Running" : "Normal"}.</p>
          </article>

          <article className="nc-panel" data-testid="backfill-progress">
            <h2>Backfill Progress</h2>
            {status.backfill.map((b) => {
              const v = pct(b.games_complete, b.games_finished);
              return (
                <div className="nc-backfill" key={b.season} data-testid={`backfill-season-${b.season}`}>
                  <div className="nc-bhead"><strong>{b.label}</strong><span>box scores <b>{b.games_complete}/{b.games_finished}</b> · days {b.dates_done}/{b.dates_total}</span><em className={v >= 100 ? "done" : ""} data-testid={`backfill-pct-${b.season}`}>{pctText(v)}%</em></div>
                  <div className="nc-progress"><span className={v >= 100 ? "done" : ""} style={{ width: `${v}%` }} /></div>
                </div>
              );
            })}
            <div className="nc-counts" data-testid="db-counts">{Object.entries(status.counts).map(([k, val]) => `${k.replace("_", " ")} ${fmt(val)}`).join(" · ")}</div>
          </article>

          <article className="nc-panel" data-testid="export-csv-card">
            <h2>Export Player Games (CSV)</h2>
            <p className="nc-muted">Processed player game logs (current season). Columns: player, game, min, pts, reb, ast, fg3m, starter.</p>
            <label>Team<select value={team} onChange={(e) => setTeam(e.target.value)} data-testid="export-team-select">
              {(teams && teams.length ? teams.map((t) => t.code) : ["DAL"]).map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
            <button className="primary" style={{ marginTop: 12, width: "100%", justifyContent: "center" }} disabled={exporting} onClick={exportCsv} data-testid="export-csv-btn">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download CSV
            </button>
          </article>
        </section>
      )}

      <section className="nc-panel nc-logs" data-testid="monitoring-center">
        <div className="nc-toolbar">
          <div className="nc-tabs">{TABS.map((t) => <button key={t} className={tab === t ? "active" : ""} onClick={() => setTab(t)} data-testid={`monitor-tab-${t.replace(/\s/g, "-").toLowerCase()}`}>{t}</button>)}</div>
          <div className="nc-filters">
            <select aria-label="Log type" value={type} onChange={(e) => setType(e.target.value)} data-testid="log-type-filter"><option value="all">All types</option>{["live", "historical", "odds", "lineups", "rotations", "system", "standings"].map((t) => <option key={t} value={t}>{t}</option>)}</select>
            <input aria-label="Search logs" placeholder="Search logs…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="log-search" />
          </div>
        </div>

        {tab === "Live Monitor" && (
          <div className="nc-jobs" data-testid="live-monitor">
            {runningJobs.length ? runningJobs.map(([name, j]) => <div key={name}>▶ <b>{name}</b> — {j.progress?.step || "running"} {j.progress?.current ?? ""}{j.progress?.total ? `/${j.progress.total}` : ""} {j.progress?.season ? `(${j.progress.season})` : ""}</div>) : <div>No jobs are running right now.</div>}
          </div>
        )}
        {tab === "API Usage" && status && (
          <div className="nc-jobs" data-testid="api-usage"><div>Calls today: <b>{fmt(status.calls_today)}</b> / {fmt(status.daily_cap)}</div><div>Regular left: <b>{fmt(regularLeft)}</b> · Live reserve left: <b>{fmt(liveLeft)}</b></div><div className="nc-muted">Counters are the real backend budget; never reset from the UI.</div></div>
        )}
        {tab === "System Status" && status && (
          <div className="nc-jobs" data-testid="system-status">
            <div>Daily job: <b>{jobs.daily?.running ? "running" : jobs.daily?.finished_at ? `last ${dateTime(jobs.daily.finished_at)}` : "never"}</b></div>
            <div>Backfill: <b>{jobs.backfill?.running ? "running" : jobs.backfill?.finished_at ? `last ${dateTime(jobs.backfill.finished_at)}` : "never"}</b></div>
            <div>Lineup history: <b>{jobs.starters?.running ? "running" : jobs.starters?.finished_at ? `last ${dateTime(jobs.starters.finished_at)}` : "never"}</b></div>
            <div className="nc-counts">{Object.entries(status.counts).map(([k, val]) => `${k.replace("_", " ")} ${fmt(val)}`).join(" · ")}</div>
          </div>
        )}

        {tab !== "Live Monitor" && (
          <div className="nc-table-scroll">
            <table data-testid="update-log-table">
              <thead><tr>{["Date & Time", "Type", "Details", "Calls", "Games", "Records", "Status", "Action"].map((h) => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.run_at + l._i} data-testid={`log-${l._i}`}>
                    <td>{dateTime(l.run_at)}</td>
                    <td><span className={`nc-tag ${(l.type || "").toLowerCase()}`}>{l.type}</span></td>
                    <td>{l.details?.trigger ? `${l.type} · ${l.details.trigger}` : l.type}</td>
                    <td>{fmt(l.calls_used)}</td>
                    <td>{fmt(l.games_added)}</td>
                    <td>{fmt(l.rows_added)}</td>
                    <td><span className={`nc-status ${l._status}`} data-testid={`log-status-${l._i}`}>{l.errors?.length ? `${l.errors.length} error(s)` : "success"}</span></td>
                    <td><button className="nc-icon-btn" onClick={() => setDetail(l)} data-testid={`log-details-${l._i}`}>View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!rows.length && <p className="nc-empty">No records match the current filters.</p>}
          </div>
        )}
      </section>

      <p className="nc-section-label">Data Management</p>
      <section className="nc-duo">
        <article className="nc-panel" data-testid="team-data-manager">
          <h2>Team Data Manager</h2>
          <p className="nc-muted">Targeted per-team refresh.</p>
          <label>Team<select value={tmTeam} onChange={(e) => setTmTeam(e.target.value)}><option value="">Select team</option>{(teams || []).map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}</select></label>
          <label>Season<select value={tmSeason} onChange={(e) => setTmSeason(e.target.value)}><option>2026-27</option><option>2025-26</option></select></label>
          <div className="nc-checks">{["Player stats", "Team stats", "Lineups", "Rotations", "Odds"].map((d) => <label key={d}><input type="checkbox" defaultChecked disabled /> {d}</label>)}</div>
          <button disabled title="No backend endpoint — targeted team refresh not available yet" data-testid="team-run-btn" style={{ width: "100%", justifyContent: "center" }}><PlayCircle className="h-4 w-4" />Run for Selected Team</button>
          <p className="nc-muted" style={{ color: "#ffb66b" }}>Not available yet — no authorized backend endpoint. Use Update Now / Backfill above.</p>
        </article>

        <article className="nc-panel" data-testid="manual-data-tools">
          <h2>Manual Data Tools</h2>
          <div className="nc-tool-grid">
            {SUPPORTED_TOOLS.map((t) => (
              <button key={t.id} className="nc-tool" disabled={busy || status?.jobs?.[t.id]?.running} onClick={() => onTask(t.id)} data-testid={`tool-${t.id}`}>
                {pending === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}<span>{t.label}</span><small>{t.sub}</small>
              </button>
            ))}
            {UNSUPPORTED_TOOLS.map((t) => (
              <button key={t.id} className="nc-tool" disabled title="No authorized backend endpoint" data-testid={`tool-${t.id}`}>
                <span>{t.label}</span><span className="nc-soon">NOT AVAILABLE</span>
              </button>
            ))}
          </div>
          <p className="nc-muted">Each action calls a real, authorized backend endpoint. Greyed tools have no endpoint and never show fake success.</p>
        </article>
      </section>

      {children}

      <DetailsModal job={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
